import React, { useState } from 'react';
import { STUDIO_COPY } from '../config';
import type { LscSupportIssueMessage } from '../../lsc/types';

type SupportThreadProps = {
  messages: LscSupportIssueMessage[];
  onSend: (body: string) => Promise<void>;
  disabled?: boolean;
  replyLabel?: string;
};

const SupportThread: React.FC<SupportThreadProps> = ({
  messages,
  onSend,
  disabled,
  replyLabel = STUDIO_COPY.adminSupportReply,
}) => {
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);

  const send = async () => {
    const body = draft.trim();
    if (!body || busy || disabled) return;
    setBusy(true);
    try {
      await onSend(body);
      setDraft('');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-3" data-testid="support-thread">
      <ul className="space-y-2">
        {messages.map((message) => (
          <li
            key={message.id}
            className="rounded border border-white/10 p-3"
            data-testid="support-message"
          >
            <p className="text-xs uppercase tracking-wide text-[var(--studio-muted)]">
              {message.author_role} · {message.created_at || ''}
            </p>
            <p
              className="whitespace-pre-wrap break-words"
              data-testid="support-message-body"
            >
              {message.body}
            </p>
          </li>
        ))}
      </ul>
      <label className="block text-sm space-y-1">
        <span className="sr-only">{STUDIO_COPY.supportReplyPlaceholder}</span>
        <textarea
          className="w-full min-h-[80px] rounded bg-black/40 p-2"
          value={draft}
          disabled={busy || disabled}
          onChange={(event) => setDraft(event.target.value)}
          placeholder={STUDIO_COPY.supportReplyPlaceholder}
          data-testid="support-reply-box"
        />
      </label>
      <button
        type="button"
        className="studio-btn-primary"
        disabled={busy || disabled || !draft.trim()}
        onClick={() => void send()}
        data-testid="support-reply-send"
      >
        {replyLabel}
      </button>
    </div>
  );
};

export default SupportThread;
